import type { TitleSize } from '@tempo-io/roadmap-dto';
import { cn } from '@tempo-io/sui-class-utils';
import { type Dispatch, memo, type SetStateAction, useMemo } from 'react';
import type { LabelWithField } from '@/components/ui/swimlane/types';
import { VALID_DROP_TARGET_OPACITY } from '@/lib/utils/loop-visualization/colors';
import { ITEM_LANE_HEIGHT, ITEM_LANE_HEIGHT_COMPACT, TimelineItemContainer } from './TimelineItem';
import type { TimelineItemType, TimelineRowType } from './useTimelineTableColumns';
import { lightenTimelineBgColor } from './utils/timelineColorUtils';
import { calculateLanesByOrder } from './utils/timelineStackingUtils';

const calculateItemOffset = (
  item: TimelineItemType,
  allItemsInRow: TimelineItemType[],
  expandedChildren: { [key: number]: boolean },
  laneHeight: number,
): number => {
  const currentOrder = item.order || 0;
  const lanesByOrder = calculateLanesByOrder(allItemsInRow, expandedChildren);

  let offset = 0;
  for (const [order, lanes] of lanesByOrder.entries()) {
    if (order >= currentOrder) {
      break;
    }
    offset += lanes * laneHeight;
  }
  return offset;
};

export const TimelineItemCell = memo(
  ({
    itemsForInterval,
    intervalStart,
    from,
    to,
    canResize,
    canDragBetweenHeaders,
    canDragDates,
    rowColor,
    itemColorById,
    itemLabelsById,
    alignNameLeft,
    dayWidth,
    titleSize,
    isRowDraggedOver,
    isDropForbidden,
    expandedChildren,
    showChildItems,
    setExpandedChildren,
    onResizeEnd,
    onItemClick,
    row,
    isCompact,
    allItemsInRow,
    headerLeftOffset,
    extraLanes,
    progressFieldId,
    systemProgressMap,
    hideName = false,
    isPublishedMode = false,
    showDates = true,
    timelineItemClassName,
    timelineItemTextColor,
    selectedIds,
    previewIds,
    onSelectionClick,
  }: {
    itemsForInterval: TimelineItemType[];
    intervalStart: Date;
    from: string;
    to: string;
    canResize: boolean;
    canDragBetweenHeaders: boolean;
    canDragDates: boolean;
    rowColor: string;
    itemColorById?: Record<number, string>;
    itemLabelsById?: Map<number, LabelWithField[]>;
    alignNameLeft?: boolean;
    dayWidth: number;
    titleSize: TitleSize;
    showChildItems: boolean;
    isRowDraggedOver: boolean;
    isDropForbidden: boolean;
    expandedChildren: Record<string, boolean>;
    setExpandedChildren: Dispatch<
      SetStateAction<{
        [key: number]: boolean;
      }>
    >;
    onResizeEnd?: (itemId: number, newStartDate: string, newEndDate: string) => void;
    onItemClick: (itemId: number) => void;
    row?: TimelineRowType;
    isCompact?: boolean;
    allItemsInRow: TimelineItemType[];
    headerLeftOffset: number;
    extraLanes?: number;
    progressFieldId: number | null;
    systemProgressMap: Map<number, number> | null;
    hideName?: boolean;
    isPublishedMode?: boolean;
    showDates?: boolean;
    timelineItemClassName?: string;
    timelineItemTextColor?: string;
    selectedIds?: ReadonlySet<number>;
    previewIds?: ReadonlySet<number> | null;
    onSelectionClick?: (
      itemId: number,
      modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean },
    ) => boolean;
  }) => {
    const laneHeight = isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;

    const totalLanes = useMemo(() => {
      const lanesByOrder = calculateLanesByOrder(allItemsInRow, expandedChildren);
      const baseLanes = Array.from(lanesByOrder.values()).reduce((sum, lanes) => sum + lanes, 0);
      return Math.max(baseLanes, 1) + (extraLanes ?? 0);
    }, [allItemsInRow, expandedChildren, extraLanes]);

    const requiredHeight = Math.max(totalLanes * laneHeight, laneHeight);

    return (
      <div
        className={cn('relative w-full border-b border-bg-subtle')}
        style={{
          height: requiredHeight,
          cursor: isDropForbidden ? 'not-allowed' : undefined,
          ...(isCompact && isRowDraggedOver
            ? { backgroundColor: lightenTimelineBgColor(rowColor, VALID_DROP_TARGET_OPACITY) }
            : {}),
        }}
      >
        <div
          className="relative w-full h-full"
          style={{
            left: headerLeftOffset,
          }}
        >
          {itemsForInterval.map((item) => {
            const containerTopOffset = calculateItemOffset(
              item,
              allItemsInRow,
              expandedChildren,
              laneHeight,
            );
            const isSelected = selectedIds?.has(item.id) ?? false;
            const isInPreview = previewIds?.has(item.id) ?? false;
            const inMultiSelect = isSelected && (selectedIds?.size ?? 0) > 1;

            return (
              <TimelineItemContainer
                isCompact={isCompact ?? false}
                canResize={canResize}
                canDragBetweenHeaders={canDragBetweenHeaders}
                canDragDates={canDragDates}
                timelineStartDate={from}
                timelineEndDate={to}
                key={item.id}
                item={item}
                color={itemColorById?.[item.id] ?? rowColor}
                labels={itemLabelsById?.get(item.id)}
                alignNameLeft={alignNameLeft}
                dayWidth={dayWidth}
                titleSize={titleSize}
                intervalStart={intervalStart}
                isExpanded={(showChildItems && expandedChildren[item.id]) ?? false}
                setExpandedChildren={setExpandedChildren}
                onResizeEnd={onResizeEnd}
                onItemClick={onItemClick}
                row={row}
                itemColorById={itemColorById}
                containerTopOffset={containerTopOffset}
                progressFieldId={progressFieldId}
                systemProgressMap={systemProgressMap}
                hideName={hideName}
                isPublishedMode={isPublishedMode}
                showDates={showDates}
                timelineItemClassName={timelineItemClassName}
                timelineItemTextColor={timelineItemTextColor}
                isSelected={isSelected || isInPreview}
                isInMultiSelectDrag={inMultiSelect}
                getSelectedItemIds={() =>
                  inMultiSelect && selectedIds ? Array.from(selectedIds) : null
                }
                onSelectionClick={onSelectionClick}
              />
            );
          })}
        </div>
      </div>
    );
  },
);

TimelineItemCell.displayName = 'TimelineItemCell';
